import { useEffect, useMemo, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import ReactMarkdown from "react-markdown";
import api from "@/lib/api";
import { useT } from "@/i18n";
import { useSiteContent } from "@/context/SiteContentContext";

// Public invite page. Tracks open + live scroll % (max) + email submission.
// Layout: two columns on desktop — statement on the left, sticky apply form
// on the right so "count me in" is always in view. Stacks on mobile.
export default function InvitePage() {
    const { token } = useParams();
    const { lang } = useT();
    const { content } = useSiteContent();
    const [invite, setInvite] = useState(null);
    const [notFound, setNotFound] = useState(false);
    const [applicantName, setApplicantName] = useState("");
    const [email, setEmail] = useState("");
    const [note, setNote] = useState("");
    const [social, setSocial] = useState("");
    const [status, setStatus] = useState("");
    const readRef = useRef({ percent: 0 });

    // Admin-editable copy for the apply panel. All defaults are set here so
    // if the admin never touches these fields the page still reads sensibly.
    const st = (key, fallback) => content[`${key}_${lang}`] || content[`${key}_en`] || fallback;
    const applyKicker = st("invite_apply_kicker", lang === "es" ? "¿te interesa?" : "interested?");
    const applyTitle  = st("invite_apply_title",  lang === "es" ? "cuéntanos." : "tell us.");
    const applyDesc   = st("invite_apply_desc",
        lang === "es"
            ? "escribe tu nombre, qué te apasiona y (si quieres) alguna red social. te contactamos pronto."
            : "your name, what you love, and (if you want) any social. we'll be in touch.");
    const applyCta    = st("invite_apply_cta",    lang === "es" ? "quiero participar →" : "count me in →");
    const okMsg       = st("invite_apply_ok",     lang === "es" ? "recibido. gracias — te contactamos pronto." : "received. thanks — we'll be in touch.");
    const errMsg      = st("invite_apply_err",    lang === "es" ? "error — intenta de nuevo." : "error — try again.");
    const lblName     = st("invite_field_name",   lang === "es" ? "tu nombre" : "your name");
    const lblEmail    = st("invite_field_email",  lang === "es" ? "tu correo" : "your email");
    const lblNote     = st("invite_field_note",   lang === "es" ? "qué te apasiona" : "what you love");
    const lblSocial   = st("invite_field_social", lang === "es" ? "@instagram / soundcloud / etc (opcional)" : "@instagram / soundcloud / etc (optional)");
    // Field visibility toggles — admin can hide any of these.
    const showName    = content.invite_field_show_name    !== "0";
    const showNote    = content.invite_field_show_note    !== "0";
    const showSocial  = content.invite_field_show_social  !== "0";

    // Load invite + fire-and-forget open ping
    useEffect(() => {
        if (!token) return;
        api.get(`/invites/public/${token}`)
            .then((r) => {
                setInvite(r.data);
                api.post(`/invites/public/${token}/open`).catch(() => {});
            })
            .catch(() => setNotFound(true));
    }, [token]);

    // Live scroll tracking — heartbeat every 5s + sendBeacon on unload so the
    // max read % lands in the DB even without a form submission.
    useEffect(() => {
        if (!token) return;
        let lastSent = 0;
        const url = `${api.defaults.baseURL}/invites/public/${token}/read`;
        const flush = (useBeacon = false) => {
            const pct = readRef.current.percent;
            if (pct <= lastSent) return;
            const payload = JSON.stringify({ read_percent: pct });
            if (useBeacon && navigator.sendBeacon) {
                navigator.sendBeacon(url, new Blob([payload], { type: "application/json" }));
            } else {
                api.post(`/invites/public/${token}/read`, { read_percent: pct }).catch(() => {});
            }
            lastSent = pct;
        };
        const onScroll = () => {
            const doc = document.documentElement;
            const total = doc.scrollHeight - window.innerHeight;
            const pct = total > 0 ? Math.min(100, Math.round((window.scrollY / total) * 100)) : 0;
            if (pct > readRef.current.percent) readRef.current.percent = pct;
        };
        const heartbeat = setInterval(() => flush(false), 5000);
        const onHide = () => flush(true);
        window.addEventListener("scroll", onScroll, { passive: true });
        window.addEventListener("pagehide", onHide);
        window.addEventListener("beforeunload", onHide);
        setTimeout(onScroll, 500);
        return () => {
            clearInterval(heartbeat);
            window.removeEventListener("scroll", onScroll);
            window.removeEventListener("pagehide", onHide);
            window.removeEventListener("beforeunload", onHide);
            flush(true);
        };
    }, [token]);

    const md = useMemo(() => {
        return content[`member_statement_${lang}`] || content.member_statement_en || "";
    }, [content, lang]);

    const submit = async (e) => {
        e.preventDefault();
        if (!email.trim()) return;
        setStatus("sending");
        try {
            await api.post(`/invites/public/${token}/submit`, {
                email: email.trim(),
                applicant_name: applicantName.trim(),
                note: note.trim(),
                social: social.trim(),
                read_percent: readRef.current.percent,
            });
            setStatus("ok");
            setApplicantName("");
            setEmail("");
            setNote("");
            setSocial("");
        } catch {
            setStatus("err");
        }
    };

    if (notFound) {
        return (
            <div data-testid="invite-not-found" className="px-4 md:px-8 py-20 min-h-[60vh]">
                <div className="font-mono text-xs uppercase tracking-[0.3em] text-[#777]">404</div>
                <h1 className="mt-3 font-heading font-black text-4xl md:text-6xl uppercase tracking-tighter">
                    {lang === "es" ? "invitación no encontrada" : "invite not found"}<span className="text-[color:var(--sc-accent)]">.</span>
                </h1>
                <p className="mt-4 font-body text-base opacity-70 max-w-md">
                    {lang === "es" ? "el link puede haber sido cancelado o mal escrito." : "the link may have been revoked or mistyped."}
                </p>
            </div>
        );
    }

    if (!invite) {
        return <div className="p-16 font-mono text-xs uppercase tracking-[0.3em] opacity-60" data-testid="invite-loading">loading…</div>;
    }

    return (
        <div data-testid="invite-page" className="px-4 md:px-8 py-8 md:py-14 min-h-[70vh]">
            <div className="max-w-6xl mx-auto grid grid-cols-1 lg:grid-cols-3 gap-10 lg:gap-14">
                {/* LEFT — statement + personal message */}
                <div className="lg:col-span-2 min-w-0">
                    <div className="font-mono text-[10px] uppercase tracking-[0.35em] text-[color:var(--sc-accent)] mb-3" data-testid="invite-badge">
                        {lang === "es" ? "invitación privada" : "private invitation"}
                        {invite.name ? <> · <span className="text-current">{invite.name}</span></> : null}
                    </div>

                    {/* Personal message from admin — shown big + accented if present. */}
                    {invite.personal_message && (
                        <div
                            data-testid="invite-personal-message"
                            className="mb-10 border-l-4 pl-5 md:pl-6 py-2 font-body text-lg md:text-2xl leading-[1.4] whitespace-pre-line"
                            style={{ borderColor: "var(--sc-accent)" }}
                        >
                            {invite.personal_message}
                        </div>
                    )}

                    {/* Statement markdown — bumped one size step vs. the /member view. */}
                    <div className="member-md invite-md">
                        {md ? (
                            <ReactMarkdown>{md}</ReactMarkdown>
                        ) : (
                            <div className="font-mono text-xs opacity-60">no statement yet.</div>
                        )}
                    </div>
                </div>

                {/* RIGHT — sticky apply form. Stacks under the statement on mobile;
                    pinned to the viewport on desktop so it's visible while reading. */}
                <aside className="lg:sticky lg:top-6 lg:self-start" data-testid="invite-apply">
                    <div
                        className="border p-6 md:p-7"
                        style={{ borderColor: "var(--sc-accent)", backgroundColor: "var(--sc-card)" }}
                    >
                        <div className="font-mono text-[10px] uppercase tracking-[0.35em] text-[color:var(--sc-accent)] mb-3">
                            {applyKicker}
                        </div>
                        <h3 className="font-heading font-black text-2xl md:text-3xl uppercase tracking-tight leading-tight mb-3">
                            {applyTitle}
                        </h3>
                        <p className="font-body text-sm md:text-base leading-relaxed opacity-80 mb-5">
                            {applyDesc}
                        </p>
                        <form onSubmit={submit} className="flex flex-col gap-3">
                            {showName && (
                                <input
                                    value={applicantName}
                                    onChange={(e) => setApplicantName(e.target.value)}
                                    placeholder={lblName}
                                    data-testid="invite-applicant-name"
                                    className="w-full bg-transparent border border-current/50 font-mono text-sm p-3 focus:border-[color:var(--sc-accent)] focus:outline-none"
                                />
                            )}
                            <input
                                type="email"
                                required
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                placeholder={lblEmail}
                                data-testid="invite-email"
                                className="w-full bg-transparent border border-current/50 font-mono text-sm p-3 focus:border-[color:var(--sc-accent)] focus:outline-none"
                            />
                            {showNote && (
                                <textarea
                                    rows={3}
                                    value={note}
                                    onChange={(e) => setNote(e.target.value)}
                                    placeholder={lblNote}
                                    data-testid="invite-note"
                                    className="w-full bg-transparent border border-current/50 font-mono text-sm p-3 focus:border-[color:var(--sc-accent)] focus:outline-none resize-y"
                                />
                            )}
                            {showSocial && (
                                <input
                                    value={social}
                                    onChange={(e) => setSocial(e.target.value)}
                                    placeholder={lblSocial}
                                    data-testid="invite-social"
                                    className="w-full bg-transparent border border-current/50 font-mono text-sm p-3 focus:border-[color:var(--sc-accent)] focus:outline-none"
                                />
                            )}
                            <button
                                type="submit"
                                disabled={status === "sending"}
                                data-testid="invite-submit"
                                className="mt-1 hover:opacity-80 transition-opacity px-6 py-3 font-mono text-[11px] uppercase tracking-[0.3em]"
                                style={{ backgroundColor: "var(--sc-accent)", color: "var(--sc-bg)" }}
                            >
                                {status === "sending" ? "…" : applyCta}
                            </button>
                        </form>
                        {status === "ok" && (
                            <div className="mt-4 font-mono text-[10px] uppercase tracking-[0.25em] text-[color:var(--sc-accent)]" data-testid="invite-ok">
                                {okMsg}
                            </div>
                        )}
                        {status === "err" && (
                            <div className="mt-4 font-mono text-[10px] uppercase tracking-[0.25em] text-[#B91C1C]" data-testid="invite-err">
                                {errMsg}
                            </div>
                        )}
                    </div>
                </aside>
            </div>
        </div>
    );
}
